import React, { useState } from "react";
import { Tooltip } from "antd";
import { RobotOutlined } from "@ant-design/icons";
import { Button, Toast } from "../../../components/ui";
import { useAuth } from "../../../hooks/auth";
import { ACTION, FEATURE } from "../../../helpers/permissions";
import { dreAiConfigApi } from "../api/dreAiConfigApi";
import AiSuggestionModal from "./AiSuggestionModal";

// Status que o interceptor de services/api.jsx já transforma em toast (não duplicar a mensagem).
const HANDLED_BY_INTERCEPTOR = new Set([400, 401, 404, 500]);

const DEFAULT_TOOLTIP = "Analisa a estrutura do DRE e as categorias com IA e sugere a configuração";

/**
 * Botão "Configurar com IA" de um submenu de "% Indicadores" do DRE ou da configuração do DFC.
 * @param {string} target dre | kpi | financialGroups | dfc
 * @param {Record<number, string>} typeLabels rótulo de cada tipo (ordem de exibição no preview)
 * @param {boolean} hasExisting se o submenu já tem vínculos (habilita a escolha substituir/completar)
 * @param {(suggestion: object, mode: 'replace'|'complete') => void} onApply aplica no estado da tela
 * @param {string} [tooltip] texto de ajuda do botão habilitado
 * @param {boolean} [allowReplace] false = só completa o que falta; vínculos existentes nunca são alterados
 * @param {(suggestion: object) => object} [filterSuggestion] ajusta a sugestão antes do preview
 */
const AiAutoConfigButton = ({
  syncId,
  target,
  typeLabels,
  hasExisting,
  disabled,
  disabledReason,
  onApply,
  tooltip = DEFAULT_TOOLTIP,
  allowReplace = true,
  filterSuggestion,
}) => {
  const { can } = useAuth();
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  // Só UX: o backend valida permissão de edição de integrações e acesso ao Sync.
  const canEdit = can(FEATURE.INTEGRATIONS, [ACTION.EDIT]) || can(FEATURE.INTEGRATIONS, [ACTION.MANAGE]);
  if (!canEdit) return null;

  const handleClick = async () => {
    setLoading(true);
    try {
      const result = await dreAiConfigApi.suggest(syncId, target);
      setSuggestion(filterSuggestion ? filterSuggestion(result) : result);
    } catch (error) {
      const status = error?.response?.status;
      if (error?.response && !HANDLED_BY_INTERCEPTOR.has(status)) {
        Toast.error(
          status === 403
            ? "Você não tem permissão para configurar esta empresa."
            : error.response.data?.message || "Não foi possível gerar a sugestão com IA.",
        );
      }
      console.error("Erro ao gerar sugestão de configuração por IA:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (mode) => {
    onApply(suggestion, mode);
    setSuggestion(null);
    Toast.success('Sugestão aplicada — revise e clique em "Salvar Alterações".');
  };

  return (
    <>
      <Tooltip title={disabled ? disabledReason : tooltip}>
        <span>
          <Button icon={<RobotOutlined />} loading={loading} disabled={disabled || loading} onClick={handleClick}>
            {loading ? "Analisando..." : "Configurar com IA"}
          </Button>
        </span>
      </Tooltip>
      <AiSuggestionModal
        open={!!suggestion}
        suggestion={suggestion}
        typeLabels={typeLabels}
        hasExisting={hasExisting}
        allowReplace={allowReplace}
        onApply={handleApply}
        onCancel={() => setSuggestion(null)}
      />
    </>
  );
};

export default AiAutoConfigButton;
